/**
 * Admin → Klantvereisten (23 okt 2026): werkafspraken per klantgroep (bv. alle Schneider-entiteiten).
 * - punten + wiki-link verschijnen als rustig blok op project, draaidag en callsheet;
 * - vlaggen: freelancers tekenen geheimhouding, AI-log bijhouden, geen portfolio-gebruik;
 * - goedgekeurde AI-tools (keuzelijst in het AI-log) en het kwartaalrapport (CSV);
 * - (main) de geheimhoudingsverklaringen van freelancers (op develop: Leveranciers → Geheimhouding).
 * Tarieven staan NIET hier: die horen bij de prijsafspraken in de Offerte Generator.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, FormControlLabel, MenuItem, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { SectionHeader } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { leesJson, meldFout } from '../../../utils/apiFout';
import { bevestig } from '../../../utils/bevestig';
import { wisKlantVereisten } from '../../../utils/klantVereisten';
import { useUserRole } from '../../../contexts/UserRoleContext';
import Geheimhouding from './Geheimhouding';

const API = `${BACKEND_URL}/Api/klantvereisten/beheer`;
const STIL = '#767c7e';

const leeg = () => ({
  titel: '', punten: '', wikiUrl: '', relaties: [], ndaFreelancersVerplicht: false, aiLogVerplicht: false,
  geenPortfolio: false, aiTools: '', aiUurtarief: '', actief: true,
});
const naarForm = (g) => ({
  ...g, punten: (g.punten || []).join('\n'), aiTools: (g.aiTools || []).join('\n'), wikiUrl: g.wikiUrl || '',
  aiUurtarief: g.aiUurtarief ?? '',
  relaties: (g.relatieIds || []).map((id, i) => ({ id, naam: (g.relatieNamen || [])[i] || `Relatie ${id}` })),
});
const naarBody = (f) => ({
  titel: f.titel, punten: f.punten, wikiUrl: f.wikiUrl, ndaFreelancersVerplicht: f.ndaFreelancersVerplicht,
  aiLogVerplicht: f.aiLogVerplicht, geenPortfolio: f.geenPortfolio, aiTools: f.aiTools, aiUurtarief: f.aiUurtarief,
  actief: f.actief, relatieIds: f.relaties.map((r) => r.id), relatieNamen: f.relaties.map((r) => r.naam).join('\n'),
});

function RelatieKiezer({ waarde, onChange }) {
  const [opties, setOpties] = useState([]);
  const [tekst, setTekst] = useState('');
  useEffect(() => {
    if (tekst.trim().length < 2) { setOpties([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setOpties(await leesJson(await authFetch(`${API}/relaties?q=${encodeURIComponent(tekst.trim())}`), 'Relaties zoeken'));
      } catch (e) { meldFout(e, 'Relaties zoeken'); }
    }, 300);
    return () => clearTimeout(t);
  }, [tekst]);
  return (
    <Autocomplete multiple size="small" options={opties} value={waarde} filterOptions={(x) => x}
      getOptionLabel={(o) => `${o.naam} (${o.id})`} isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, v) => onChange(v)} onInputChange={(_e, v) => setTekst(v)}
      noOptionsText={tekst.length < 2 ? 'Typ minstens 2 letters' : 'Geen Gripp-relatie gevonden'}
      renderTags={(v, getTagProps) => v.map((o, i) => <Chip size="small" label={`${o.naam} (${o.id})`} {...getTagProps({ index: i })} key={o.id} />)}
      renderInput={(p) => <TextField {...p} label="Gripp-relaties" helperText="Alle entiteiten van deze klant (bv. BV, België, Frankrijk)" />} />
  );
}

function Rapport({ groep }) {
  const nu = new Date();
  const [jaar, setJaar] = useState(nu.getFullYear());
  const [kwartaal, setKwartaal] = useState(Math.floor(nu.getMonth() / 3) + 1);
  const [data, setData] = useState(null);
  const url = (csv) => `${API}/${groep.id}/ai-rapport?jaar=${jaar}&kwartaal=${kwartaal}${csv ? '&csv=true' : ''}`;
  const bekijk = async () => {
    try { setData(await leesJson(await authFetch(url(false)), 'Rapport laden')); } catch (e) { meldFout(e, 'Rapport laden'); }
  };
  const download = async () => {
    try {
      const r = await authFetch(url(true));
      if (!r.ok) await leesJson(r, 'CSV downloaden');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ai-rapportage-${groep.sleutel}-${jaar}-q${kwartaal}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { meldFout(e, 'CSV downloaden'); }
  };
  const t = data?.totaal;
  const eur = (x) => (x == null ? '—' : `€ ${Number(x).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <TextField select size="small" label="Kwartaal" value={kwartaal} onChange={(e) => setKwartaal(Number(e.target.value))} sx={{ width: 110 }}>
          {[1, 2, 3, 4].map((k) => <MenuItem key={k} value={k}>Q{k}</MenuItem>)}
        </TextField>
        <TextField size="small" type="number" label="Jaar" value={jaar} onChange={(e) => setJaar(Number(e.target.value))} sx={{ width: 100 }} />
        <Button size="small" onClick={bekijk} sx={{ textTransform: 'none' }}>Bekijken</Button>
        <Button size="small" onClick={download} sx={{ textTransform: 'none' }}>CSV downloaden</Button>
      </Stack>
      {data && (data.regels.length === 0 ? (
        <Typography sx={{ fontSize: '0.85rem', color: STIL, mt: 1.5 }}>Geen AI-gebruik gelogd in {data.periode}.</Typography>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.85rem', color: STIL, mb: 1 }}>
            {data.periode}: {t.regels} regel(s), {t.urenAi} uur met AI tegenover {t.urenTraditioneel} uur geschat traditioneel.
            Berekend: {eur(t.kostenAi)} met AI, {eur(t.kostenTraditioneel)} traditioneel
            {t.zonderTarief ? ` (${t.zonderTarief} regel(s) zonder uurtarief alleen in uren)` : ''}.
          </Typography>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Project</TableCell><TableCell align="right">Uren AI</TableCell>
              <TableCell align="right">Uren traditioneel</TableCell><TableCell align="right">Kosten AI</TableCell>
              <TableCell align="right">Traditioneel</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {data.perProject.map((p) => (
                <TableRow key={p.projectNummer}>
                  <TableCell>{p.projectNummer}{p.projectNaam ? ` · ${p.projectNaam}` : ''}</TableCell>
                  <TableCell align="right">{p.urenAi}</TableCell><TableCell align="right">{p.urenTraditioneel}</TableCell>
                  <TableCell align="right">{eur(p.kostenAi)}</TableCell><TableCell align="right">{eur(p.kostenTraditioneel)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
}

export default function KlantVereisten() {
  const { permissions } = useUserRole() || {};
  const [groepen, setGroepen] = useState(null);
  const [bewerk, setBewerk] = useState(null);   // { id|null, form }
  const [bezig, setBezig] = useState(false);
  const [rapportVoor, setRapportVoor] = useState(null);

  const laad = useCallback(async () => {
    try { setGroepen(await leesJson(await authFetch(API), 'Klantvereisten laden')); }
    catch (e) { meldFout(e, 'Klantvereisten laden'); setGroepen([]); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const bewaar = async () => {
    setBezig(true);
    try {
      const { id, form } = bewerk;
      await leesJson(await authFetch(id ? `${API}/${id}` : API, {
        method: id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(naarBody(form)),
      }), 'Opslaan');
      toast.success('Opgeslagen');
      wisKlantVereisten();
      setBewerk(null);
      laad();
    } catch (e) { meldFout(e, 'Opslaan'); }
    finally { setBezig(false); }
  };

  const verwijder = async (g) => {
    const ok = await bevestig({
      titel: `Klantgroep ${g.titel} verwijderen?`,
      tekst: 'Het blok verdwijnt van projecten, draaidagen en callsheets. Heeft de groep al AI-logregels of geheimhoudingsverklaringen, dan kan het niet: zet hem dan op inactief.',
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      await leesJson(await authFetch(`${API}/${g.id}`, { method: 'DELETE' }), 'Verwijderen');
      wisKlantVereisten();
      setBewerk(null);
      laad();
    } catch (e) { meldFout(e, 'Verwijderen'); }
  };

  const f = bewerk?.form;
  const zet = (k) => (e) => setBewerk((b) => ({ ...b, form: { ...b.form, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e } }));

  return (
    <Box sx={{ maxWidth: 900 }}>
      <SectionHeader title="Klantvereisten"
        subtitle="Werkafspraken van klanten met strengere eisen (bv. Schneider Electric). Ze staan als rustig blok op het project, de draaidag en de callsheet." />
      <Typography sx={{ fontSize: '0.85rem', color: STIL, mb: 2 }}>
        Tarieven en ratecards beheer je bij de prijsafspraken in de Offerte Generator (Beheer → Prijsafspraken beheren); de offerte-check toetst de regels daartegen.
      </Typography>

      {/* main: de bel-melding draait alleen via de engine-voordeur (develop); hier geen schakelaar. */}

      {groepen === null ? <CircularProgress size={20} /> : (
        <Stack spacing={1.5}>
          {groepen.length === 0 && <Alert severity="info" variant="outlined">Nog geen klantgroepen.</Alert>}
          {groepen.map((g) => (
            <Box key={g.id} sx={{ border: '1px solid #e5e9eb', borderRadius: 2, p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{g.titel}{!g.actief && <Box component="span" sx={{ color: STIL, fontWeight: 400 }}> · inactief</Box>}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: STIL }}>
                    {[`${g.relatieIds.length} Gripp-relatie(s)`, g.ndaFreelancersVerplicht && 'freelancers tekenen', g.aiLogVerplicht && 'AI-log',
                      g.geenPortfolio && 'geen portfolio', !g.wikiUrl && 'nog geen wiki-link'].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {g.aiLogVerplicht && <Button size="small" onClick={() => setRapportVoor(rapportVoor === g.id ? null : g.id)} sx={{ textTransform: 'none' }}>AI-kwartaalrapport</Button>}
                  <Button size="small" onClick={() => setBewerk({ id: g.id, form: naarForm(g) })} sx={{ textTransform: 'none' }}>Bewerken</Button>
                </Stack>
              </Stack>
              {rapportVoor === g.id && <Rapport groep={g} />}
            </Box>
          ))}
          {!bewerk && <Box><Button size="small" onClick={() => setBewerk({ id: null, form: leeg() })} sx={{ textTransform: 'none' }}>+ Nieuwe klantgroep</Button></Box>}
        </Stack>
      )}

      {f && (
        <Box sx={{ mt: 3, p: 2.5, border: '1px solid #e5e9eb', borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>{bewerk.id ? `${f.titel} bewerken` : 'Nieuwe klantgroep'}</Typography>
          <Stack spacing={2}>
            <TextField size="small" label="Naam" value={f.titel} onChange={zet('titel')} />
            <RelatieKiezer waarde={f.relaties} onChange={(v) => setBewerk((b) => ({ ...b, form: { ...b.form, relaties: v } }))} />
            <TextField size="small" label="Korte punten (één per regel)" value={f.punten} onChange={zet('punten')} multiline minRows={4}
              helperText="Houd ze kort; de eerste drie staan meteen in beeld." />
            <TextField size="small" label="Wiki-link (werkafspraken)" value={f.wikiUrl} onChange={zet('wikiUrl')} placeholder="https://wiki.fullframe.nl/doc/…" />
            <Box>
              <FormControlLabel control={<Switch checked={f.ndaFreelancersVerplicht} onChange={zet('ndaFreelancersVerplicht')} />} label="Freelancers tekenen een geheimhoudingsverklaring" />
              <FormControlLabel control={<Switch checked={f.aiLogVerplicht} onChange={zet('aiLogVerplicht')} />} label="AI-gebruik per project loggen" />
              <FormControlLabel control={<Switch checked={f.geenPortfolio} onChange={zet('geenPortfolio')} />} label="Geen portfolio-gebruik" />
              <FormControlLabel control={<Switch checked={f.actief} onChange={zet('actief')} />} label="Actief" />
            </Box>
            {f.aiLogVerplicht && (
              <>
                <TextField size="small" label="Aangemelde AI-tools (één per regel)" value={f.aiTools} onChange={zet('aiTools')} multiline minRows={3} />
                <TextField size="small" type="number" label="Uurtarief voor het AI-rapport (€)" value={f.aiUurtarief} onChange={zet('aiUurtarief')}
                  helperText="Voor de berekende vergelijking; wordt per logregel vastgelegd." sx={{ maxWidth: 320 }} />
              </>
            )}
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disableElevation size="small" onClick={bewaar} disabled={bezig || !f.titel.trim()} sx={{ textTransform: 'none' }}>
                {bezig ? 'Bezig…' : 'Opslaan'}
              </Button>
              <Button size="small" onClick={() => setBewerk(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
              <Box sx={{ flex: 1 }} />
              {bewerk.id && <Button size="small" color="error" onClick={() => verwijder(groepen.find((g) => g.id === bewerk.id))} sx={{ textTransform: 'none' }}>Verwijderen</Button>}
            </Stack>
          </Stack>
        </Box>
      )}

      {(permissions?.admin_settings?.view || permissions?.hr_leveranciers?.view) && (
        <Box sx={{ mt: 5 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Geheimhoudingsverklaringen freelancers</Typography>
          <Geheimhouding magBewerken={!!(permissions?.admin_settings?.edit || permissions?.hr_leveranciers?.edit)} />
        </Box>
      )}
    </Box>
  );
}
